/**
 * Fusion Canvas demo scenarios — same catalog/card/guide shape as crypto Workbench
 * demos (DemoGuide + DemoScenarioCard), but actions drive the Fusion seed/timeline
 * instead of loading a wallet graph.
 */
import type { DemoAudience, DemoCategory, DemoGraph } from './demoGraphs';

export type FusionDemoAction =
  | { type: 'setMain'; main: 'build' | 'canvas' | 'journal' }
  | { type: 'setStep'; step: number }
  | { type: 'play'; playing?: boolean }
  | { type: 'courtSafe'; on: boolean }
  | { type: 'showDemo'; on: boolean }
  | { type: 'runPlaybook' }
  | { type: 'selectNode'; id: string }
  | { type: 'openAside'; tab: 'inspector' | 'ledger' | 'import' | 'files' };

export type FusionDemoNavHints = {
  palette?: boolean;
  courtSafe?: boolean;
  surfaces?: boolean;
  timeline?: boolean;
  inspector?: boolean;
  ledger?: boolean;
  bankImport?: boolean;
  caseFiles?: boolean;
  build?: boolean;
  journal?: boolean;
  runRail?: boolean;
  radial?: boolean;
  playbook?: boolean;
};

export type FusionDemo = {
  id: string;
  title: string;
  description: string;
  howTo: string[];
  category: DemoCategory;
  demoType?: DemoAudience;
  tags: string[];
  priority?: boolean;
  startHereOrder?: number;
  startHereBlurb?: string;
  startHereDuration?: string;
  /** Applied when the scenario is opened on /fusion. */
  actions: FusionDemoAction[];
  navHints?: FusionDemoNavHints;
};

/** Adapter so DemoGuide can render Fusion scenarios with the Workbench UI chrome. */
export function fusionDemoAsGuideDemo(demo: FusionDemo): DemoGraph {
  return {
    id: demo.id,
    title: demo.title,
    description: demo.description,
    howTo: demo.howTo,
    category: demo.category,
    demoType: demo.demoType,
    tags: demo.tags,
    priority: demo.priority,
    startHereOrder: demo.startHereOrder,
    startHereBlurb: demo.startHereBlurb,
    startHereDuration: demo.startHereDuration,
    elements: [],
  };
}

export const fusionDemos: Record<string, FusionDemo> = {
  'fusion-ingestion-replay': {
    id: 'fusion-ingestion-replay',
    title: 'Ingestion Timeline — Play the case together',
    description:
      'Click the right-side Ingestion Timeline dot to expand the scrubber, then Play stages to reveal evidence one surface at a time: bank, UPI/crypto handover, on-chain, OSINT, identity hub. Staged assembly — not transfer-by-transfer playback.',
    category: 'tutorials',
    demoType: 'feature',
    tags: ['timeline', 'play', 'multi-surface'],
    priority: true,
    startHereOrder: 1,
    startHereBlurb: 'Open the right-side Timeline dot, then Play stages.',
    startHereDuration: '~4 min',
    actions: [
      { type: 'setMain', main: 'canvas' },
      { type: 'showDemo', on: true },
      { type: 'courtSafe', on: false },
      { type: 'setStep', step: 1 },
      { type: 'play', playing: true },
    ],
    navHints: { timeline: true, surfaces: true, inspector: true, palette: true },
    howTo: [
      'Stay on the Canvas tab. Find the Ingestion Timeline chip on the right (stage-colored dot + label).',
      'Click the chip to expand the bottom bar — or let Play open it automatically.',
      'Press Play stages — Stage 1 is bank only; Stage 2 adds the UPI-to-crypto handover.',
      'Let Play continue through on-chain (3) and OSINT (4) until the gold identity hub at stage 5.',
      'Collapse with the chevron to return to the right-side dot and free canvas space.',
      'Open Legend and use Surface filters to hide one modality, then show it again.',
    ],
  },

  'fusion-court-safe': {
    id: 'fusion-court-safe',
    title: 'Court-safe — proven joins only',
    description:
      'Turn Court-safe on to dim scored (correlated) links and leave only deterministic joins: UTR, tx hash, registry. Use that view for court packs and senior briefings.',
    category: 'tutorials',
    demoType: 'feature',
    tags: ['court-safe', 'determinism', 'evidence'],
    priority: true,
    startHereOrder: 2,
    startHereBlurb: 'Toggle Court-safe and watch scored edges fade.',
    startHereDuration: '~3 min',
    actions: [
      { type: 'setMain', main: 'canvas' },
      { type: 'showDemo', on: true },
      { type: 'setStep', step: 5 },
      { type: 'play', playing: false },
      { type: 'courtSafe', on: false },
      { type: 'openAside', tab: 'inspector' },
    ],
    navHints: { courtSafe: true, ledger: true, inspector: true, surfaces: true },
    howTo: [
      'The full case is already on the canvas (all five ingestion stages). Look at solid vs dashed edges.',
      'Click Court-safe in the top bar — scored (dashed) links dim; solid UTR / hash / registry joins stay bright.',
      'Open Court-safe Chain in the right rail and read the deterministic evidence table.',
      'Turn Court-safe off — the dashed correlations come back for investigative work.',
      'Export a PNG with Court-safe on if you need a briefing still (sandbox stamps SYNTHETIC).',
    ],
  },

  'fusion-identity-hub': {
    id: 'fusion-identity-hub',
    title: 'Identity hub — one suspect, four surfaces',
    description:
      'The gold star is the cross-surface join: bank KYC, TRX wallet, Instagram handle, and company director hang off one identity. Select it and read provenance in the Fusion Inspector.',
    category: 'crime-patterns',
    demoType: 'investigation',
    tags: ['identity hub', 'cross-link', 'OSINT'],
    priority: true,
    startHereOrder: 3,
    startHereBlurb: 'Select the gold star and read the join story.',
    startHereDuration: '~3 min',
    actions: [
      { type: 'setMain', main: 'canvas' },
      { type: 'showDemo', on: true },
      { type: 'setStep', step: 5 },
      { type: 'play', playing: false },
      { type: 'courtSafe', on: false },
      { type: 'selectNode', id: 'id:rkumar' },
      { type: 'openAside', tab: 'inspector' },
    ],
    navHints: { inspector: true, surfaces: true, courtSafe: true, radial: true },
    howTo: [
      'Confirm Suspect: R. Kumar (gold star) is selected — if not, click it on the canvas.',
      'In the Fusion Inspector, read attribution tier, confidence, and source.',
      'Click a spoke (bank account or wallet) and note its module colour and shape.',
      'Right-click a node to open the Radial menu — only operators valid for that type.',
      'Turn Court-safe on — scored OSINT spokes may dim while hard joins stay.',
      'Double-click a wallet to open the crypto Workbench for on-chain drill-down.',
    ],
  },

  'fusion-autopilot': {
    id: 'fusion-autopilot',
    title: 'Autopilot + Run rail — watch every job',
    description:
      'Start Autopilot from the Palette. Each act focuses nodes and posts a job on the Run rail (bottom strip). Click a chip to jump to the node that run touched. Court-safe may flip mid-playbook.',
    category: 'tutorials',
    demoType: 'feature',
    tags: ['playbook', 'autopilot', 'run rail', 'palette'],
    priority: true,
    startHereOrder: 4,
    startHereBlurb: 'Watch jobs land on the Run rail as Autopilot runs.',
    startHereDuration: '~5 min',
    actions: [
      { type: 'setMain', main: 'canvas' },
      { type: 'showDemo', on: true },
      { type: 'setStep', step: 1 },
      { type: 'play', playing: false },
      { type: 'courtSafe', on: false },
      { type: 'runPlaybook' },
    ],
    navHints: { palette: true, playbook: true, runRail: true, courtSafe: true, radial: true },
    howTo: [
      'If the left Palette is collapsed, expand it and open Playbooks — Autopilot should already be running.',
      'Follow the lower-third act titles as the canvas focuses each step.',
      'Watch the Run rail under the canvas: each operator appears as a job chip (Running → Done).',
      'Click a Run rail chip that has a target icon — the canvas centers that node and opens the Inspector.',
      'When Court-safe turns on mid-playbook, note which edges stay bright.',
      'Stop from the narration panel when you have seen enough, then re-run Autopilot from the Palette.',
    ],
  },

  'fusion-build-journal': {
    id: 'fusion-build-journal',
    title: 'Build, Canvas, Journal — three tabs',
    description:
      'Author evidence on Build, see joins on Canvas, recap on Journal. Replay all streams the build; Up to here rolls later nodes back; From here plays forward.',
    category: 'tutorials',
    demoType: 'feature',
    tags: ['build', 'journal', 'intake', 'run rail', 'replay'],
    priority: false,
    startHereBlurb: 'Walk Build → Canvas → Journal scrub.',
    startHereDuration: '~5 min',
    actions: [
      { type: 'setMain', main: 'build' },
      { type: 'showDemo', on: true },
    ],
    navHints: { build: true, journal: true, palette: true, timeline: true, runRail: true },
    howTo: [
      'You start on Build — add or open an evidence card, fill inputs, and press Run.',
      'Switch to Canvas — the fused graph shows what Build wrote (plus demo seed if ON).',
      'Check the Run rail at the bottom — your card run should appear as a job chip.',
      'Open Journal — runs appear in Build order. Click Replay all to stream them on Canvas.',
      'Back on Journal, click Up to here on a mid-list entry — later canvas nodes cascade-hide (rollback).',
      'Click From here on an earlier entry — Play advances from that frame. Exit replay to restore the full graph.',
    ],
  },

  'fusion-journal-scrub': {
    id: 'fusion-journal-scrub',
    title: 'Journal scrub — jump, rollback, play forward',
    description:
      'With demo seed ON, Journal already has five ingestion frames. Jump to a frame to cascade-hide later work, play forward to stream it back, or Replay all from the top.',
    category: 'tutorials',
    demoType: 'feature',
    tags: ['journal', 'replay', 'rollback', 'cascade'],
    priority: true,
    startHereOrder: 7,
    startHereBlurb: 'Open Journal and scrub the demo build history.',
    startHereDuration: '~4 min',
    actions: [
      { type: 'setMain', main: 'journal' },
      { type: 'showDemo', on: true },
    ],
    navHints: { journal: true, timeline: true, inspector: true, runRail: true },
    howTo: [
      'You are on Journal with demo seed frames (bank → crypto → OSINT → identity).',
      'Leave Build order selected so #1…#5 match Replay.',
      'Click entry #3 (On-chain trace) — Canvas opens paused at that frame; later runs are rolled back.',
      'On the Case replay bar, step forward with › or Play — rolled-back nodes reappear in order.',
      'Return to Journal and press From here on #2 — playback starts at the UPI handover.',
      'Press Exit replay (or X on the bar) to show the full graph again.',
    ],
  },

  'fusion-bank-handover': {
    id: 'fusion-bank-handover',
    title: 'Bank to crypto handover',
    description:
      'Stage 2 with Court-safe on: bank account legs meet a TRX wallet on a deterministic join. Open Court-safe Chain for the handover table.',
    category: 'crime-patterns',
    demoType: 'investigation',
    tags: ['bank', 'UTR', 'off-ramp', 'handover'],
    priority: true,
    startHereBlurb: 'See the bank-crypto join that Court-safe keeps.',
    startHereDuration: '~4 min',
    actions: [
      { type: 'setMain', main: 'canvas' },
      { type: 'showDemo', on: true },
      { type: 'setStep', step: 2 },
      { type: 'play', playing: false },
      { type: 'courtSafe', on: true },
      { type: 'selectNode', id: 'account:50100' },
      { type: 'openAside', tab: 'ledger' },
    ],
    navHints: { courtSafe: true, ledger: true, inspector: true, surfaces: true, bankImport: true, caseFiles: true, timeline: true },
    howTo: [
      'Court-safe is already on — only deterministic joins stay bright at stage 2.',
      'HDFC account ••5100 is selected — read UTRs in the Fusion Inspector activity list.',
      'Court-safe Chain is open — find the bank-to-crypto handover seam in the table.',
      'Click the off-ramp wallet (TRc9k6…) and compare its TRC20 activity.',
      'Use the Ingestion timeline to step forward to stage 3 without leaving this seam.',
      'Turn Court-safe off to reveal scored correlations around the same handover.',
      'Try Bank import + Case files to land a live statement and promote it to a real case.',
    ],
  },

  'fusion-palette-evidence': {
    id: 'fusion-palette-evidence',
    title: 'Palette Evidence In — open Build and Run',
    description:
      'Every Evidence In row opens a real form: Bank Statement opens Import; Wallet / VPA / Phone / NCRP / device JSON open a Build card. Use Load sample, then Run — nodes land on Canvas and a job appears on the Run rail.',
    category: 'tutorials',
    demoType: 'feature',
    tags: ['palette', 'evidence', 'build', 'samples'],
    priority: true,
    startHereOrder: 5,
    startHereBlurb: 'Click Palette Evidence In → Build card → Load sample → Run.',
    startHereDuration: '~6 min',
    actions: [
      { type: 'setMain', main: 'canvas' },
      { type: 'showDemo', on: true },
      { type: 'setStep', step: 5 },
      { type: 'play', playing: false },
      { type: 'courtSafe', on: false },
    ],
    navHints: { palette: true, build: true, runRail: true, inspector: true, journal: true },
    howTo: [
      'Expand the Palette (left rail) and open Evidence In.',
      'Click Wallet Address — Fusion switches to Build with a wallet card. Press Load sample, then Run.',
      'You return to Canvas with a new wallet node; the Run rail shows the job. Click the chip to focus it.',
      'Back on Palette, click NCRP Complaint — Load sample SYN-0147 JSON, Run, and watch VPAs/accounts join the subject hub.',
      'Click Bank Statement — Bank import opens on Canvas (or use Build → Bank Statement + sample CSV).',
      'Click UFDR / E01 — Load sample device_extract JSON, Run, and confirm wallets/phones appear.',
      'Open Journal — each Run is an event you can Replay.',
    ],
  },

  'fusion-palette-operators': {
    id: 'fusion-palette-operators',
    title: 'Palette Operators — select then run',
    description:
      'Select a seed node, then click an Operator in the Palette (or right-click for the Radial menu). LIVE operators O1–O3 hit the case job APIs when a sandbox case is bound; WIP rows post an honest Run-rail note.',
    category: 'tutorials',
    demoType: 'feature',
    tags: ['palette', 'operators', 'radial', 'run rail'],
    priority: true,
    startHereOrder: 6,
    startHereBlurb: 'Select HDFC ••5100, then run UTR-Stitch from the Palette.',
    startHereDuration: '~5 min',
    actions: [
      { type: 'setMain', main: 'canvas' },
      { type: 'showDemo', on: true },
      { type: 'setStep', step: 5 },
      { type: 'play', playing: false },
      { type: 'courtSafe', on: false },
      { type: 'selectNode', id: 'account:50100' },
      { type: 'openAside', tab: 'inspector' },
    ],
    navHints: { palette: true, runRail: true, radial: true, inspector: true, playbook: true },
    howTo: [
      'HDFC account ••5100 should already be selected. If not, click it on the canvas.',
      'In Palette → Operators, click UTR-Stitch — a Running → Done chip appears on the Run rail.',
      'Click Off-Ramp Match with the same account selected (or a VPA node).',
      'Select the off-ramp wallet TRc9k6… and click Auto-Investigate.',
      'Try Trace or P2P Correlate — WIP chips explain they are not fully wired on Fusion yet.',
      'Right-click any node to open the Radial menu — same operators, filtered by type.',
      'Optional: run Autopilot from Playbooks to walk O1–O3 across the five-act arc.',
    ],
  },

  'fusion-build-ncrp': {
    id: 'fusion-build-ncrp',
    title: 'Build · Start from NCRP complaint',
    description:
      'Guided Start → NCRP complaint. Load the sample SYN-0147 JSON, Run, and watch victim + UPI/account beneficiaries bloom on the Canvas.',
    category: 'tutorials',
    demoType: 'feature',
    tags: ['build', 'ncrp', 'guided start', 'intake'],
    priority: true,
    startHereOrder: 10,
    startHereBlurb: 'Build tab → NCRP → Load sample → Run.',
    startHereDuration: '~3 min',
    actions: [
      { type: 'setMain', main: 'build' },
      { type: 'showDemo', on: false },
    ],
    navHints: { build: true, journal: true, inspector: true },
    howTo: [
      'On Build, click NCRP complaint (or Start over if a board is already open).',
      'Click Load sample for demo on the NCRP card.',
      'Click Run — you should land on Canvas with complaint, victim, phone, VPAs, and account.',
      'Open Journal to confirm the run event; try Suggested next step on a VPA/account.',
    ],
  },

  'fusion-build-frozen': {
    id: 'fusion-build-frozen',
    title: 'Build · Start from frozen account',
    description:
      'Guided Start → Frozen account. Load the sample bank CSV (or a real PDF if StatementFlow is configured), Run, and see account + Dr/Cr counterparties.',
    category: 'tutorials',
    demoType: 'feature',
    tags: ['build', 'bank', 'statement', 'guided start'],
    priority: true,
    startHereOrder: 11,
    startHereBlurb: 'Build → Frozen account → sample CSV → Run.',
    startHereDuration: '~3 min',
    actions: [
      { type: 'setMain', main: 'build' },
      { type: 'showDemo', on: false },
    ],
    navHints: { build: true, bankImport: true, journal: true },
    howTo: [
      'Click Frozen account on Guided Start.',
      'Load sample for demo (CSV) — or drop your own statement PDF if VITE_STATEMENTFLOW_URL is set.',
      'Run → Canvas should show the frozen account node and counterparties.',
      'Select the account and try UTR-Stitch from Suggested next step (needs UTR legs).',
    ],
  },

  'fusion-build-device': {
    id: 'fusion-build-device',
    title: 'Build · Start from seized device',
    description:
      'Guided Start → Seized device. Load device_extract.v1 sample JSON (or upload a real UFDR/E01 on a logged-in sandbox case).',
    category: 'tutorials',
    demoType: 'feature',
    tags: ['build', 'ufdr', 'device', 'guided start'],
    priority: true,
    startHereOrder: 12,
    startHereBlurb: 'Build → Seized device → sample JSON → Run.',
    startHereDuration: '~3 min',
    actions: [
      { type: 'setMain', main: 'build' },
      { type: 'showDemo', on: false },
    ],
    navHints: { build: true, journal: true, inspector: true },
    howTo: [
      'Click Seized device on Guided Start.',
      'Load sample for demo — device + wallets/VPA/phone/handle appear after Run.',
      'On Canvas, select the TRX wallet and try Auto-Investigate / Trace chips.',
      'Optional (logged in): upload a real .ufdr/.e01 instead of JSON — needs sandbox case bind.',
    ],
  },

  'fusion-build-wallet': {
    id: 'fusion-build-wallet',
    title: 'Build · Start from wallet address',
    description:
      'Guided Start → Wallet address. Load the sample WazirX TRX wallet, Run, then chain Suggested next steps.',
    category: 'tutorials',
    demoType: 'feature',
    tags: ['build', 'wallet', 'guided start'],
    priority: true,
    startHereOrder: 13,
    startHereBlurb: 'Build → Wallet → sample TRX → Run.',
    startHereDuration: '~2 min',
    actions: [
      { type: 'setMain', main: 'build' },
      { type: 'showDemo', on: false },
    ],
    navHints: { build: true, runRail: true, inspector: true },
    howTo: [
      'Click Wallet address on Guided Start.',
      'Load sample for demo (TWd4Wr…), then Run — Canvas shows a TRX-badged wallet.',
      'Use Suggested next step → Auto-Investigate or Trace (bound case / live APIs).',
    ],
  },

  'fusion-build-blank': {
    id: 'fusion-build-blank',
    title: 'Build · Blank board',
    description:
      'Guided Start → Blank board. Add evidence cards yourself (Wallet, VPA, Phone, Case note, Instagram…). For a live FIR case use New case (not this demo sandbox).',
    category: 'tutorials',
    demoType: 'feature',
    tags: ['build', 'blank', 'guided start'],
    startHereOrder: 14,
    startHereBlurb: 'Empty board — Add evidence, then Run.',
    startHereDuration: '~2 min',
    actions: [
      { type: 'setMain', main: 'build' },
      { type: 'showDemo', on: false },
    ],
    navHints: { build: true, journal: true },
    howTo: [
      'Click Blank board — you should see an empty board with Add evidence.',
      'Add Wallet, Phone, Case note, or Instagram Handle — fill values, Run each.',
      'Switch to Canvas — nodes should hang off the Subject hub; Journal lists each run.',
      'For a real FIR: click New case (title bar) to create an LE case and open Case Canvas — do not use this sandbox for live work.',
    ],
  },
};

export function listFusionDemos(): FusionDemo[] {
  return Object.values(fusionDemos);
}

export function getFusionDemo(id: string | null | undefined): FusionDemo | null {
  if (!id) return null;
  return fusionDemos[id] ?? null;
}
